<template>
  <d-container
    fluid
    class="main-content-container px-4">
    <!-- Page Header -->
    <div class="page-header-with-filters">
      <d-row
        no-gutters
        class="page-header py-4">
        <d-col
          col
          sm="6"
          class="text-center text-sm-left mb-4 mb-sm-0">
          <span class="text-uppercase page-subtitle">{{ $t('marketCustomers.marketCustomersLabel') }}</span>
          <h3 class="page-title">{{ $t('marketCustomers.marketCustomersList') }}</h3>
        </d-col>

        <d-col
          col
          sm="6"
          class="text-right text-sm-right d-flex justify-content-end align-items-end">
          <d-button
            theme="secondary"
            @click.prevent="showImportPopup = true">
            <i class="material-icons">cloud_upload</i>
            {{ $t('marketCustomers.importMarketCustomers') }}
          </d-button>
          <d-button
            theme="primary"
            @click.prevent="showImportHistoryPopup = true"
            class="ml-2">
            <i class="material-icons">history</i>
            {{ $t('marketCustomers.importHistory') }}
          </d-button>
          <d-button
            theme="success"
            @click.prevent="$router.push('/klienci-z-targow/nowy')"
            class="ml-2">
            <i class="material-icons">add</i>
            {{ $t('marketCustomers.addNewMarketCustomer') }}
          </d-button>
        </d-col>
      </d-row>

      <list-filters
        :config="filtersConfig"
        :isListLoaded="allDataLoaded"
        :view-name="'market-customers'"
        @init="handleFiltersInit"
        @search="handleFiltersSearch" />
    </div>

    <d-card style="width: 100%;">
      <table class="table mt-0 mb-0 full-width">
        <thead class="bg-light">
          <tr>
            <th
              scope="col"
              class="border-0">{{ $t('ui.name') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px;">
              {{ $t('ui.emailAddress') }}<br>
              {{ $t('customer.telephone') }}
            </th>
            <th
              scope="col"
              class="border-0">{{ $t('marketCustomers.tag') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 300px;">{{ $t('marketCustomers.otherData') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 120px;">{{ $t('ui.lastContactAt') }}</th>
            <th
              scope="col"
              class="border-0"
              style="width: 240px;">{{ $t('marketCustomers.accountManager') }}</th>
            <th
              scope="col"
              class="border-0">{{ $t('ui.actions') }}</th>
          </tr>
        </thead>
        <tbody v-if="allDataLoaded && !loadError">
          <tr
            v-for="(customer, index) of customers"
            :key="'customer-' + index">
            <td class="align-middle">
              {{ customer.name }}
            </td>
            <td class="align-middle">
              <template v-if="customer.email">
                {{ customer.email }}<br>
              </template>
              {{ customer.phone }}
            </td>
            <td class="align-middle">
              {{ customer.tag }}
            </td>
            <td class="align-middle">
              <small>
                <template v-if="customer.contact_name || customer.contact_surname">
                  {{ customer.contact_name }} {{ customer.contact_surname }}<br>
                </template>

                <template v-if="customer.invoice_postcode || customer.invoice_city || customer.invoice_street || customer.invoice_building || customer.invoice_locale || customer.invoice_country_prefix || customer.invoice_tax_number || customer.country">
                  <template v-if="customer.invoice_street || customer.invoice_building || customer.invoice_locale">
                    {{ customer.invoice_street }} {{ customer.invoice_building }} <template v-if="customer.invoice_locale">/ {{ customer.invoice_locale }}</template><br>
                  </template>
                  <template v-if="customer.invoice_postcode || customer.invoice_city">{{ customer.invoice_postcode }} {{ customer.invoice_city }}<br></template>
                  <template v-if="customer.country">{{ customer.country }}<br></template>
                  {{ customer.invoice_country_prefix }} {{ customer.invoice_tax_number }}
                </template>
              </small>
            </td>
            <td class="align-middle">
              {{ customer.last_contact_at }}
            </td>
            <td class="align-middle">
              <template v-if="customer.account_manager">
                {{ customer.account_manager.name }}
              </template>
              <template v-else>
                &mdash;
              </template>
            </td>

            <td
              class="align-middle"
              style="width: 150px;">
              <d-button
                theme="primary"
                @click.native="$router.push('/klienci-z-targow/edycja/' + customer.id)"
                class="mr-2"
                :title="$t('ui.edit')">
                <i class="material-icons">edit</i>
              </d-button>
              <d-button
                v-if="userIsAdmin"
                theme="danger"
                :disabled="removeInProgress"
                @click.native="removeMarketCustomer(customer.id)"
                :title="$t('ui.delete')">
                <i class="material-icons">delete</i>
              </d-button>
            </td>
          </tr>
          <tr v-if="customers.length === 0">
            <td
              colspan="7"
              class="text-center">
              {{ $t('marketCustomers.noMarketCustomersToDisplay') }}&hellip;
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-if="!allDataLoaded && !loadError"
        class="pb-3 pt-3 text-center">
        {{ $t('ui.dataLoading') }}&hellip;
      </div>

      <d-alert
        v-if="loadError"
        show
        theme="warning"
        class="text-center">
        {{ $t('ui.dataLoadingFailed') }} <a
          href="javascript:window.location.reload();"
        class="alert-link">{{ $t('ui.refreshPage') }}</a> {{ $t('ui.toTryAgain') }}
      </d-alert>
    </d-card>

    <import-popup
      v-if="showImportPopup" />

    <import-history-popup
      v-if="showImportHistoryPopup" />

    <pagination
      :meta-data="paginationMetaData"
      :initial-page="currentPage" />
  </d-container>
</template>

<script>
import debounce from 'lodash.debounce';
import FormUtils from './../utils/FormUtils.js';
import HasPagination from './../mixins/HasPagination.vue';
import ImportHistoryPopup from './../components/market-customers/ImportHistoryPopup.vue';
import ImportPopup from './../components/market-customers/ImportPopup.vue';
import ListUtils from './../utils/ListUtils.js';
import Pagination from './../components/common/Pagination.vue';
import CalendarUtils from '@/utils/CalendarUtils.js';
import cloneDeep from 'lodash.clonedeep';

export default {
  name: 'market-customers',
  mixins: [
    HasPagination
  ],
  components: {
    'import-history-popup': ImportHistoryPopup,
    'import-popup': ImportPopup,
    'pagination': Pagination
  },
  computed: {
    allDataLoaded () {
      return this.loaded && this.loadedWorkers;
    },
    userIsAdmin () {
      return this.$store.getters.getUserType === 'admin';
    },
    filtersConfig () {
      return [
        {
          key: 'search',
          apiKey: 'search',
          label: this.$t('ui.search'),
          placeholder: this.$t('marketCustomers.marketCustomersSearchPlaceholder'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
        },
        {
          key: 'country',
          apiKey: 'country',
          label: this.$t('settings.country.country'),
          placeholder: this.$t('customer.customerCountryQuestionMark'),
          type: 'text',
          isBasic: true,
          defaultValue: '',
        },
        {
          key: 'last_contact',
          label: this.$t('ui.lastContactAt'),
          type: 'daterange',
          isBasic: true,
          defaultValue: { startDate: null, endDate: null },
          apiTransform: (val, where) => {
            if (val.startDate) {
              where['last_contact_from'] = CalendarUtils.formatDateForPHP(new Date(val.startDate));
            }
            if (val.endDate) {
              where['last_contact_to'] = CalendarUtils.formatDateForPHP(new Date(val.endDate));
            }
          }
        },
        {
          key: 'tag',
          apiKey: 'tag',
          label: this.$t('marketCustomers.tag'),
          placeholder: this.$t('marketCustomers.marketCustomersTagPlaceholder'),
          type: 'text',
          isBasic: false,
          defaultValue: ''
        },
        {
          key: 'account_manager_id',
          apiKey: 'account_manager_id',
          label: this.$t('users.selectWorker'),
          placeholder: this.$t('users.selectWorker'),
          type: 'select',
          isBasic: false,
          defaultValue: null,
          options: this.workers.map(w => ({ text: w.name, value: w.id }))
        }
      ];
    }
  },
  data () {
    return {
      customers: [],
      loaded: false,
      loadError: false,
      loadedWorkers: false,
      showImportHistoryPopup: false,
      showImportPopup: false,
      removeInProgress: false,
      workers: [],
      activeFilters: {},
      apiQueryPayload: {}
    };
  },
  mounted () {
    this.loadAdditionalData();
    this.$bus.$on('market-customers-reload-needed', this.resetPagination);
    this.$bus.$on('market-customers-import-popup-close', this.closeImportPopup);
    this.$bus.$on('market-customers-import-history-popup-close', this.closeImportHistoryPopup);
  },
  methods: {
    handleFiltersInit (filters, apiPayload) {
      this.activeFilters = filters;
      this.apiQueryPayload = apiPayload;

      this.loadFilteredData();
    },
    handleFiltersSearch (activeFilters, apiPayload) {
      this.activeFilters = activeFilters;
      this.apiQueryPayload = apiPayload;
      this.$bus.$emit('pagination-reset');
      this.debouncedLoadFilteredData();
    },
    loadAdditionalData () {
      FormUtils.loadAdditionalData(this, {
        endpoint: '/users/items',
        outputKey: 'workers',
        loadedKey: 'loadedWorkers',
        errorKey: 'loadError',
        params: {
          return: 'get',
          where: {
            type: ['admin', 'worker']
          }
        },
        successAction: () => {
          this.workers = [{
            id: 0,
            name: this.$t('ui.lack')
          }].concat(this.workers);
        }
      });
    },
    debouncedLoadFilteredData: debounce(function () {
      this.loadFilteredData();
    }, 250),
    loadFilteredData () {
      let where = cloneDeep(this.apiQueryPayload) || {};

      if (this.activeFilters.account_manager_id === 0) {
        where.account_manager_id = 0;
      }

      ListUtils.loadPaginatedItemsData(this, {
        endpoint: '/market-customers/items?page=' + this.currentPage,
        listField: 'customers',
        params: {
          where
        }
      });
    },
    removeMarketCustomer (customerID) {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/market-customers/delete/',
        id: customerID,
        successTitle: this.$t('marketCustomers.deleteSuccessTitle'),
        successText: this.$t('marketCustomers.deleteSuccessText'),
        confirmTitle: this.$t('marketCustomers.deleteConfirmTitle'),
        confirmText: this.$t('ui.deleteConfirmText'),
        errorTitle: this.$t('ui.errorOccured'),
        errorText: this.$t('marketCustomers.deleteErrorText'),
        successAction: () => {
          this.removeInProgress = false;
          this.loadFilteredData();
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    closeImportHistoryPopup () {
      this.showImportHistoryPopup = false;
    },
    closeImportPopup () {
      this.showImportPopup = false;
    }
  },
  beforeDestroy () {
    this.$bus.$off('market-customers-reload-needed', this.resetPagination);
    this.$bus.$off('market-customers-import-popup-close', this.closeImportPopup);
    this.$bus.$off('market-customers-import-history-popup-close', this.closeImportHistoryPopup);
  }
};
</script>
